/**
 * Minimal database executor abstraction.
 *
 * All SQL in the application flows through this interface. Production uses a
 * Tauri SQL-plugin implementation (SQLite on disk). Tests use a
 * better-sqlite3 implementation so migrations and repositories are exercised
 * against real SQLite in Node. A dev-only in-memory implementation supports
 * previewing the UI in a plain browser.
 *
 * Keeping this boundary narrow also supports the specification's requirement
 * (Section 10) that a future move to a centralized API/database replaces the
 * persistence boundary rather than the application.
 *
 * SQL placeholders use `?` positional parameters, which all three
 * implementations support.
 *
 * TRANSACTIONS (Phase 8): `transaction(fn)` runs `fn` atomically —
 * BEGIN IMMEDIATE … COMMIT, ROLLBACK on throw. Calls are REENTRANT: a
 * nested `transaction` joins the outer one (no savepoints), so services
 * can compose atomic flows (place object → feature → assembly → lines)
 * without caring whether a caller already opened a transaction. Standard
 * SQL only — PostgreSQL-portable.
 */
export interface DbExecutor {
  select<T = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<T[]>;
  execute(sql: string, params?: unknown[]): Promise<void>;
  /** Run `fn` atomically; reentrant (nested calls join the outer transaction). */
  transaction<T>(fn: () => Promise<T>): Promise<T>;
  /** Observer-only barrier: call outside transaction bodies before reading
   * a committed estimate. Never wait on this from the work being committed. */
  whenIdle?(): Promise<void>;
  close(): Promise<void>;
}

/** Coordinator events may occur inside a composed outer transaction. A
 * financial observer must wait for its commit/rollback, or it can publish
 * half an assembly while the drawing already looks saved. */
export function createTransactionObserver(): { begin: () => () => void; whenIdle: () => Promise<void> } {
  const pending = new Set<Promise<void>>();
  return {
    begin() {
      let finish!: () => void;
      const promise = new Promise<void>((resolve) => { finish = resolve; });
      pending.add(promise);
      return () => { pending.delete(promise); finish(); };
    },
    async whenIdle() {
      while (pending.size > 0) await Promise.all([...pending]);
    },
  };
}

/**
 * Shared transaction wrapper used by every executor implementation: tracks
 * reentrancy and issues BEGIN IMMEDIATE / COMMIT / ROLLBACK through the
 * executor's own `execute`.
 */
export function createTransactionRunner(executeRaw: (sql: string) => Promise<void>): {
  transaction: <T>(fn: () => Promise<T>) => Promise<T>;
  whenIdle: () => Promise<void>;
} {
  let inTransaction = false;
  const observer = createTransactionObserver();
  return {
    whenIdle: observer.whenIdle,
    async transaction<T>(fn: () => Promise<T>): Promise<T> {
      if (inTransaction) return fn();
      inTransaction = true;
      const settled = observer.begin();
      try {
        await executeRaw("BEGIN IMMEDIATE");
        const result = await fn();
        await executeRaw("COMMIT");
        return result;
      } catch (err) {
        try {
          await executeRaw("ROLLBACK");
        } catch {
          /* connection-level failure; original error matters more */
        }
        throw err;
      } finally {
        inTransaction = false;
        settled();
      }
    },
  };
}
